import { PersonIdentity } from '@/components/person-identity'
import { useEffect, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { HugeiconsIcon } from '@hugeicons/react'
import { Search01Icon, UnfoldMoreIcon } from '@hugeicons/core-free-icons'
import { studentApi } from '@/modules/students/students.service'
import { contactApi } from '@/modules/contacts/contacts.api'
import { contactDisplayName } from '@/modules/contacts/contacts.columns'
import { teacherApi } from '@/modules/teachers/teachers.service'
import { buildWaNumber, formatWaNumber } from '../whatsapp.utils'
import {
  RECIPIENT_TYPE_LABELS,
  type WhatsappRecipientType,
} from '../whatsapp.types'
import { cn, toTitleCase } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'

export interface RecipientOption {
  id: string
  type: WhatsappRecipientType
  name: string // "lastName firstName", title-cased
  firstName?: string // nombre de pila, para las variables de plantilla
  phone: string // international, '' when none on file
  email: string | null
  gender?: 'male' | 'female' | null
  dateOfBirth?: string | null
  kind?: 'person' | 'company'
}

type Filter = 'all' | WhatsappRecipientType

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'student', label: 'Estudiantes' },
  { key: 'guardian', label: 'Contactos' },
  { key: 'teacher', label: 'Profesores' },
]

const TYPE_TONE: Record<WhatsappRecipientType, string> = {
  student: 'bg-sky-500/10 text-sky-600',
  guardian: 'bg-violet-500/10 text-violet-600',
  teacher: 'bg-amber-500/10 text-amber-600',
}

async function fetchRecipients(
  filter: Filter,
  q: string,
): Promise<RecipientOption[]> {
  const params = { q: q || undefined, limit: 20 }
  const jobs: Promise<RecipientOption[]>[] = []
  if (filter === 'all' || filter === 'student') {
    jobs.push(
      studentApi.list(params).then((res) =>
        res.data.map((r) => ({
          id: r.id,
          gender: r.gender,
          dateOfBirth: r.dateOfBirth,
          type: 'student' as const,
          name: toTitleCase(`${r.lastName} ${r.firstName}`),
          firstName: toTitleCase(r.firstName),
          phone: buildWaNumber(r.phone, r.phoneCountryCode),
          email: r.email ?? null,
        })),
      ),
    )
  }
  if (filter === 'all' || filter === 'guardian') {
    jobs.push(
      contactApi.list(params).then((res) =>
        res.data.map((r) => ({
          id: r.id,
          gender: r.gender,
          dateOfBirth: r.dateOfBirth,
          type: 'guardian' as const,
          kind: r.kind,
          name: contactDisplayName(r),
          firstName: toTitleCase(r.firstName),
          phone: buildWaNumber(r.phone, r.phoneCountryCode),
          email: r.email ?? null,
        })),
      ),
    )
  }
  if (filter === 'all' || filter === 'teacher') {
    jobs.push(
      teacherApi.list(params).then((res) =>
        res.data.map((r) => ({
          id: r.id,
          gender: r.gender,
          dateOfBirth: r.dateOfBirth,
          type: 'teacher' as const,
          name: toTitleCase(`${r.lastName} ${r.firstName}`),
          firstName: toTitleCase(r.firstName),
          phone: buildWaNumber(r.phone, r.phoneCountryCode),
          email: r.email ?? null,
        })),
      ),
    )
  }
  const groups = await Promise.all(jobs)
  return groups.flat().sort((a, b) => a.name.localeCompare(b.name, 'es'))
}

// Unified picker over students / contacts / teachers. A single search with
// type filter chips; returns the selected entity with its type and phone.
export function RecipientCombobox({
  onChange,
}: {
  onChange: (next: RecipientOption) => void
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState<Filter>('all')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isFetching } = useQuery({
    queryKey: ['whatsapp-recipient', filter, debounced],
    queryFn: () => fetchRecipients(filter, debounced),
    enabled: open,
    placeholderData: keepPreviousData,
  })
  const options = data ?? []
  const loading = isFetching && options.length === 0

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-11 w-full justify-start gap-2 font-normal text-muted-foreground"
        >
          <HugeiconsIcon icon={Search01Icon} className="size-4 shrink-0" />
          <span className="flex-1 truncate text-left">
            Busca un contacto, estudiante o profesor…
          </span>
          <HugeiconsIcon
            icon={UnfoldMoreIcon}
            className="size-4 shrink-0 opacity-60"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar por nombre…"
            value={search}
            onValueChange={setSearch}
          />
          <div className="flex flex-wrap gap-1 border-b px-2 py-2">
            {FILTERS.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setFilter(option.key)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  filter === option.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <CommandList
            className="max-h-[min(60vh,340px)] overscroll-contain"
            onWheel={(event) => event.stopPropagation()}
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Spinner className="size-4" />
                Buscando…
              </div>
            ) : (
              <>
                <CommandEmpty>Sin resultados.</CommandEmpty>
                {options.map((option) => (
                  <CommandItem
                    key={`${option.type}-${option.id}`}
                    value={`${option.type}-${option.id}`}
                    onSelect={() => {
                      onChange(option)
                      setOpen(false)
                    }}
                    className="gap-3"
                  >
                    <PersonIdentity person={option} className="flex-1" detail={option.phone ? formatWaNumber(option.phone) : 'Sin teléfono'} />
                    <span
                      className={cn(
                        'shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium',
                        TYPE_TONE[option.type],
                      )}
                    >
                      {RECIPIENT_TYPE_LABELS[option.type]}
                    </span>
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
